/*
 * recemment-consultes.js
 * Comportement de sections/recemment-consultes.liquid.
 *
 * 1. Sur une fiche produit, le produit courant est mis en tete de la liste
 *    memorisee dans localStorage (cle « rp:recently-viewed », 20 produits au
 *    plus, sans doublon).
 * 2. La section affiche les produits de la liste, sauf le produit courant,
 *    en recuperant chaque carte par l'API de rendu des sections
 *    (/products/<handle>?section_id=recemment-consultes-carte).
 * 3. Un produit qui n'existe plus ou n'est plus publie (404) sort de la liste.
 * 4. Les fleches (reglage de la section) font defiler la rangee ; elles se
 *    desactivent aux deux extremites.
 *
 * Aucune donnee ne quitte le navigateur. Si localStorage est indisponible
 * (navigation privee stricte), la section reste simplement invisible.
 */
(() => {
  const CLE = 'rp:recently-viewed';
  const MEMOIRE_MAX = 20;

  function lire() {
    try {
      const brut = JSON.parse(localStorage.getItem(CLE) || '[]');
      return Array.isArray(brut) ? brut.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  }

  function ecrire(liste) {
    try {
      localStorage.setItem(CLE, JSON.stringify(liste.slice(0, MEMOIRE_MAX)));
    } catch (e) {
      /* stockage refuse : on continue sans memoire */
    }
  }

  function memoriser(handle) {
    if (!handle) return;
    ecrire([handle, ...lire().filter((h) => h !== handle)]);
  }

  function racine() {
    const r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.endsWith('/') ? r : `${r}/`;
  }

  class RecemmentConsultes extends HTMLElement {
    connectedCallback() {
      if (this.dejaInitialise) return;
      this.dejaInitialise = true;

      const courant = this.dataset.currentHandle || '';
      memoriser(courant);
      this.afficher(courant);
    }

    async afficher(courant) {
      const liste = this.querySelector('[data-recent-liste]');
      if (!liste) return;

      const max = Math.max(1, parseInt(this.dataset.max, 10) || 4);
      const section = this.dataset.cardSection || 'recemment-consultes-carte';
      const handles = lire().filter((h) => h !== courant).slice(0, max);
      if (handles.length === 0) return;

      const cartes = await Promise.all(
        handles.map(async (handle) => {
          try {
            const rep = await fetch(`${racine()}products/${encodeURIComponent(handle)}?section_id=${section}`);
            if (rep.status === 404) return { handle, absent: true };
            if (!rep.ok) return { handle };
            const doc = new DOMParser().parseFromString(await rep.text(), 'text/html');
            const carte = doc.querySelector('[data-recent-card]');
            return { handle, carte: carte ? document.importNode(carte, true) : null };
          } catch (e) {
            return { handle };
          }
        })
      );

      const absents = cartes.filter((c) => c.absent).map((c) => c.handle);
      if (absents.length) ecrire(lire().filter((h) => !absents.includes(h)));

      const valides = cartes.filter((c) => c.carte);
      liste.replaceChildren(...valides.map((c) => c.carte));
      if (valides.length > 0) this.hidden = false;
      this.brancherFleches(liste);
    }

    brancherFleches(liste) {
      const prec = this.querySelector('[data-rc-precedent]');
      const suiv = this.querySelector('[data-rc-suivant]');
      if (!prec || !suiv) return;

      const maj = () => {
        const reste = liste.scrollWidth - liste.clientWidth - liste.scrollLeft;
        prec.disabled = liste.scrollLeft < 2;
        suiv.disabled = reste < 2;
      };
      const pas = () => Math.max(liste.clientWidth * 0.85, 1);
      const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const defiler = (sens) => liste.scrollBy({ left: sens * pas(), behavior: reduit ? 'auto' : 'smooth' });

      if (!this.fleches) {
        prec.addEventListener('click', () => defiler(-1));
        suiv.addEventListener('click', () => defiler(1));
        liste.addEventListener('scroll', maj, { passive: true });
        window.addEventListener('resize', maj);
        this.fleches = true;
      }
      maj();
    }
  }

  if (!customElements.get('recemment-consultes')) {
    customElements.define('recemment-consultes', RecemmentConsultes);
  }
})();
